import { ALLOWED_SEARCH_FILTER_CATEGORY_TYPES } from "@javascript/constants/search/search_constants";




/**
 * Removes a specified criteria value (and its HTML encoded version) from a search filter type.
 * The search filter types covered by FacetSearchStore.data.filterCriteria (and therefore this helper) are:
 *
 *  - conditions
 *  - organizations
 *  - therapies
 *
 * It then updates the filter state, if necessary, and triggers a new search if a value was removed.
 *
 * @param {string} criteriaType - The type of filter criteria (e.g. 'conditions', 'organizations', 'therapies')
 * @param {string} criteriaValue - The value to remove from the filter
 * @returns {void} Returns early if value not found in filter
 *
 * @example
 * // Remove "Acme Corp" from organizations filter
 * removeSearchCriteriaFilter('organizations', 'Acme Corp');
 *
 * // Remove "Bloating" from conditions filter
 * removeSearchCriteriaFilter('conditions', 'Bloating');
 */

export const removeSearchCriteriaFilter = (criteriaType, criteriaValue) => {
  const currentCriteriaValues = FacetSearchStore.data.filterCriteria[criteriaType];
  const encodedValue = escapeHtml(criteriaValue);

  if (!_.include(currentCriteriaValues, criteriaValue) && !_.include(currentCriteriaValues, encodedValue)) {
    return;
  }

  const filteredCriteriaValues = _.filter(currentCriteriaValues, (value) => {
    const isNotMatch = value !== criteriaValue;
    const isNotEncodedMatch = value !== escapeHtml(criteriaValue);
    return isNotMatch && isNotEncodedMatch;
  });

  if (filteredCriteriaValues.length > 0) {
    FacetSearchStoreActions.updateFilterCriteria(criteriaType, filteredCriteriaValues);
  } else {
    FacetSearchStoreActions.removeFilterCriteria(criteriaType);
  }

  // Since we modified the current criteria values, trigger a search
  doEventSearch();
};

/**
 * Adds a search criteria value to a specified filter type if not already present.
 * Creates a new filter array if none exists for the type.
 * Optionally triggers a new search when filter is modified.
 *
 * @param {string} criteriaType - The type of filter criteria (e.g. 'conditions', 'organizations', 'therapies')
 * @param {string} criteriaValue - The value to add to the filter
 * @param {boolean} [shouldSearch=true] - Whether to trigger a new search after modifying filter
 * @returns {void} Returns early if value already exists in filter
 *
 * @example
 * // Add "Acme Corp" to organizations filter and search
 * addSearchCriteriaFilter('organizations', 'Acme Corp');
 *
 * // Add "Bloating" to conditions filter without searching
 * addSearchCriteriaFilter('conditions', 'Bloating', false);
 */
export const addSearchCriteriaFilter = (criteriaType, criteriaValue, shouldSearch = true) => {
  const currentCriteriaValues = FacetSearchStore.data.filterCriteria[criteriaType];
  const isExistingFilter = currentCriteriaValues !== undefined;

  // If a filter for a criteria type exists, check if value is already included
  if (isExistingFilter && _.include(currentCriteriaValues, criteriaValue)) {
    return;
  }

  // Update or create the filter
  if (isExistingFilter) {
    FacetSearchStoreActions.addToFilterCriteria(criteriaType, criteriaValue);
  } else {
    FacetSearchStoreActions.updateFilterCriteria(criteriaType, [criteriaValue]);
  }

  // Trigger search if requested
  if (shouldSearch) {
    doEventSearch();
  }
};


/**
 * Tracks the state change of a filter category
 * @param {boolean} isOpen - Whether the filter category is being opened (true) or closed (false)
 * @param {string} entity_type - The type of entity being filtered (one of the allowed values)
 */
export const trackSearchFilterCategoryState = (isOpen, categoryType) => {
  let trackingEventText = isOpen ? "opened_filter_category" : "closed_filter_category";
  let trackingText = isOpen ? "Opened Filter Category" : "Closed Filter Category";

  // Validate that categoryType is one of the allowed values
  if (!ALLOWED_SEARCH_FILTER_CATEGORY_TYPES.includes(categoryType)) {
    console.warn(`Invalid categoryType: ${categoryType}. Must be one of: ${ALLOWED_SEARCH_FILTER_CATEGORY_TYPES.join(", ")}`);
    return; // Don't track if categoryType is invalid
  }

  track(trackingEventText, trackingText, { categoryType: categoryType });
};
